/**
 * Servicios para la gestión de archivos de participantes.
 *
 * Este módulo contiene todos los servicios necesarios para interactuar con la API
 * del backend en relación a los archivos de carga masiva de participantes,
 * incluyendo operaciones de listado, descarga y consulta de reportes.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-12]
 * @updated [Fecha de última modificación: 2025-08-12]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { fetchWrapper } from "../../../fetchWrapper";

/**
 * Servicio para obtener la lista de archivos de carga masiva de una actividad
 * @param {string} activityId - ID de la actividad
 * @param {number} pageIndex - Índice de la página
 * @param {number} pageSize - Tamaño de la página
 * @returns {Object} Respuesta del servidor con la lista de archivos
 */
export const ParticipantFilesListService = async (activityId, pageIndex, pageSize) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/participants/uploads/activity/${activityId}/?page=${pageIndex}&pageSize=${pageSize}`;

    const result = await fetchWrapper(url, {
      method: "GET",
    });

    return result;
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

/**
 * Servicio para obtener el reporte detallado de un archivo de carga
 * @param {string} uploadId - ID del archivo de carga
 * @returns {Object} Respuesta del servidor con el reporte detallado
 */
export const ParticipantFileReportService = async (uploadId) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/participants/uploads/report/${uploadId}/`;

    const result = await fetchWrapper(url, {
      method: "GET",
    });

    return result;
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

/**
 * Servicio para descargar un archivo de carga masiva
 * @param {string} uploadId - ID del archivo de carga
 * @returns {Blob} Archivo para descarga
 */
export const ParticipantFileDownloadService = async (uploadId) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/participants/uploads/download/${uploadId}/`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
    });

    // Si la respuesta no es exitosa, se lee el error (consumiendo el stream) y se retorna
    if (!response.ok) {
      // Aquí no clonamos: si la respuesta es un error, consumimos el body y salimos.
      const errorResult = await response.json();
      return {
        status: false,
        message: errorResult.message || "Error del servidor al realizar la solicitud",
      };
    }

    // Evitamos llamar a clone() ya que podría estar afectando en algunos casos.
    const responseClone = response.clone();
    const originalBlob = await responseClone.blob();
    // Crear un nuevo blob con el tipo MIME específico
    console.log(response.headers.get("Content-Disposition"));
    const fileBlob = new Blob([originalBlob], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    return {
      status: true,
      file: fileBlob,
      contentDisposition: response.headers.get("Content-Disposition"),
    };
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    throw new Error(error.message || message);
  }
};
